import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, X } from "lucide-react";
import { FadeUp } from "@/components/Reveal";
import { API } from "@/portal/AuthContext";

const CAT_LABEL = { film: "Film", audio: "Audio", agentur: "Agentur" };

const mediaKind = (filename, contentType = "") => {
  const ext = (filename.split(".").pop() || "").toLowerCase();
  if (["jpg", "jpeg", "png", "webp", "gif", "avif", "svg"].includes(ext)) return "image";
  if (["mp4", "mov", "webm", "mkv", "m4v"].includes(ext)) return "video";
  if (["mp3", "wav", "m4a", "ogg", "aac", "flac"].includes(ext)) return "audio";
  return "other";
};

const fmtTime = (s) => {
  if (!isFinite(s) || !s) return "–";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

const Bars = ({ active, count = 9, className = "h-6 items-center", maxOpacity = 1 }) => (
  <span className={`flex gap-[2.5px] ${className}`} aria-hidden="true">
    {Array.from({ length: count }).map((_, i) => (
      <span
        key={i}
        className={`block w-px bg-gold ${active ? "wave-bar" : ""}`}
        style={{
          height: `${25 + Math.abs(Math.sin(i * 1.1)) * 75}%`,
          animationDelay: `${i * 0.09}s`,
          animationDuration: `${0.9 + (i % 3) * 0.25}s`,
          opacity: active ? maxOpacity : 0.3,
        }}
      />
    ))}
  </span>
);

const PlayButton = ({ active, size = "w-12 h-12", icon = 16 }) => (
  <span
    className={`${size} shrink-0 rounded-full border flex items-center justify-center transition-[border-color,background-color] duration-500 ${
      active ? "border-gold bg-gold/10" : "border-paper/40 group-hover:border-gold group-hover:bg-gold/10"
    }`}
  >
    {active
      ? <Pause size={icon} className="text-gold" fill="currentColor" />
      : <Play size={icon} className="text-paper ml-0.5 group-hover:text-gold transition-colors duration-500" fill="currentColor" />}
  </span>
);

const ProgressLine = ({ time, dur, onSeek, testId }) => (
  <span
    role="slider"
    tabIndex={0}
    aria-valuenow={Math.round(time)}
    aria-valuemin={0}
    aria-valuemax={Math.round(dur || 0)}
    className="block w-full h-5 relative cursor-pointer group/prog"
    onClick={(e) => { e.stopPropagation(); onSeek(e); }}
    aria-label="Abspielposition"
    data-testid={testId}
  >
    <span className="absolute inset-x-0 top-1/2 h-px bg-paper/20" />
    <span
      className="absolute left-0 top-1/2 h-px bg-gold transition-[width] duration-150"
      style={{ width: dur ? `${(time / dur) * 100}%` : "0%" }}
    />
  </span>
);

const HOVER_PREVIEW_SECONDS = 7;

const VideoPoster = ({ id, src }) => {
  const [failed, setFailed] = useState(false);
  const [hover, setHover] = useState(false);
  const [playing, setPlaying] = useState(false);
  const timer = useRef(null);
  const stopTimer = useRef(null);
  const videoRef = useRef(null);
  const wrapRef = useRef(null);
  const cls = "w-full h-full object-cover pointer-events-none transition-transform duration-[1600ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]";
  const canHover = typeof window !== "undefined" && !("ontouchstart" in window && window.matchMedia?.("(hover: none)").matches);

  const enter = () => {
    if (!canHover) return;
    timer.current = setTimeout(() => setHover(true), 350);
  };
  const leave = () => {
    clearTimeout(timer.current);
    clearTimeout(stopTimer.current);
    setHover(false);
    setPlaying(false);
  };
  useEffect(() => {
    const host = wrapRef.current?.parentElement;
    if (!host) return undefined;
    host.addEventListener("mouseenter", enter);
    host.addEventListener("mouseleave", leave);
    return () => {
      host.removeEventListener("mouseenter", enter);
      host.removeEventListener("mouseleave", leave);
      clearTimeout(timer.current);
      clearTimeout(stopTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCanPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.duration && isFinite(v.duration) && v.duration > 12) v.currentTime = Math.min(v.duration * 0.3, 20);
    v.play().then(() => {
      setPlaying(true);
      stopTimer.current = setTimeout(() => { setPlaying(false); setTimeout(() => setHover(false), 600); }, HOVER_PREVIEW_SECONDS * 1000);
    }).catch(() => {});
  };

  const [thumbTry, setThumbTry] = useState(0);
  const onThumbError = () => {
    // Vorschau wird ggf. gerade erst erzeugt – nach kurzer Wartezeit erneut versuchen, bevor auf das Video zurückgefallen wird.
    if (thumbTry < 3) setTimeout(() => setThumbTry((t) => t + 1), [2500, 6000, 12000][thumbTry]);
    else setFailed(true);
  };

  if (failed) return <video src={`${src}#t=0.5`} preload="metadata" muted playsInline className={cls} aria-hidden="true" />;
  return (
    <span ref={wrapRef} className="absolute inset-0 block" data-testid={`reference-hover-${id}`}>
      <img src={`${API}/references/${id}/thumb${thumbTry ? `?r=${thumbTry}` : ""}`} alt="" loading="lazy" onError={onThumbError} className={cls} data-testid={`reference-poster-${id}`} />
      {hover && (
        <video
          ref={videoRef}
          src={src}
          muted
          playsInline
          preload="auto"
          onCanPlay={onCanPlay}
          onEnded={() => setPlaying(false)}
          className={`absolute inset-0 w-full h-full object-cover pointer-events-none transition-opacity duration-500 ${playing ? "opacity-100" : "opacity-0"}`}
          aria-hidden="true"
          data-testid={`reference-hover-video-${id}`}
        />
      )}
    </span>
  );
};

const ReferenceGallery = ({ category }) => {
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);

  // shared audio state: one track at a time
  const audioRef = useRef(null);
  const [currentId, setCurrentId] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);
  const [durMap, setDurMap] = useState({});

  useEffect(() => {
    fetch(`${API}/references?category=${category}`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setItems)
      .catch(() => setItems([]));
  }, [category]);

  useEffect(() => {
    const el = new Audio();
    el.preload = "none";
    audioRef.current = el;
    const onTime = () => setTime(el.currentTime);
    const onMeta = () => setDur(el.duration);
    const onEnd = () => { setPlaying(false); };
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onMeta);
    el.addEventListener("ended", onEnd);
    return () => {
      el.pause();
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onMeta);
      el.removeEventListener("ended", onEnd);
    };
  }, []);

  if (!items || items.length === 0) return null;

  const url = (id) => `${API}/references/${id}/file`;
  const tagged = items
    .map((it) => ({ ...it, kind: mediaKind(it.filename, it.content_type) }))
    .filter((it) => it.kind !== "other");
  const visuals = tagged.filter((it) => it.kind === "image" || it.kind === "video");
  const audios = tagged.filter((it) => it.kind === "audio");

  if (tagged.length === 0) return null;

  const toggleAudio = (it) => {
    const el = audioRef.current;
    if (!el) return;
    if (currentId === it.id && playing) {
      el.pause();
      setPlaying(false);
      return;
    }
    if (currentId !== it.id) {
      el.src = url(it.id);
      setCurrentId(it.id);
      setTime(0);
    }
    el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !dur) return;
    const r = e.currentTarget.getBoundingClientRect();
    el.currentTime = ((e.clientX - r.left) / r.width) * dur;
  };

  const openModal = (it) => {
    if (audioRef.current) audioRef.current.pause();
    setPlaying(false);
    setActive(it);
  };

  // kompakter Rhythmus: 3 Kacheln je Reihe, jede 4. Reihe eine breitere Kachel als Akzent
  const span = (i, n) => {
    if (n === 1) return "md:col-span-6 md:col-start-4 aspect-[16/10]";
    if (n === 2) return "md:col-span-6 aspect-[16/10]";
    if (n >= 7 && i % 7 === 0) return "md:col-span-8 aspect-[16/9]";
    if (n >= 7 && i % 7 === 1) return "md:col-span-4 aspect-[16/10]";
    return "md:col-span-4 aspect-[16/10]";
  };


  const featuredAudio = audios.length === 1 && visuals.length === 0;

  return (
    <section className="shell py-20 md:py-28 border-t hairline" data-testid={`reference-gallery-${category}`} aria-label="Referenzen">
      <FadeUp>
        <p className="label-text text-gold mb-12 md:mb-16">Ausgewählte Arbeiten · {CAT_LABEL[category]}</p>
      </FadeUp>

      {visuals.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-12 gap-5 md:gap-6">
          {visuals.map((it, i) => (
            <FadeUp key={it.id} delay={(i % 3) * 0.08} className={span(i, visuals.length)}>
              <button
                type="button"
                onClick={() => openModal(it)}
                data-testid={`reference-item-${it.id}`}
                className="group relative block w-full h-full overflow-hidden bg-ink-2"
                aria-label="Referenz öffnen"
              >
                {it.kind === "image" && (
                  <img src={url(it.id)} alt="Referenzprojekt von DIMEDIAL" loading="lazy" className="w-full h-full object-cover transition-transform duration-[1600ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.05]" />
                )}
                {it.kind === "video" && (
                  <>
                    <VideoPoster id={it.id} src={url(it.id)} />
                    <span className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
                      <span className="w-14 h-14 rounded-full border border-paper/50 bg-black/30 backdrop-blur-sm flex items-center justify-center group-hover:border-gold group-hover:bg-gold/10 transition-[border-color,background-color] duration-500">
                        <Play size={18} className="text-paper ml-0.5" fill="currentColor" />
                      </span>
                    </span>
                  </>
                )}
              </button>
            </FadeUp>
          ))}
        </div>
      )}

      {featuredAudio && (
        <FadeUp>
          {(() => {
            const it = audios[0];
            const isCurrent = currentId === it.id;
            const isPlaying = isCurrent && playing;
            return (
              <div className="relative overflow-hidden border hairline bg-gradient-to-br from-ink-2 to-ink" data-testid={`reference-audio-${it.id}`}>
                <div className="absolute inset-x-0 bottom-0 h-24 md:h-32 flex items-end gap-[3px] px-6 md:px-12 opacity-50 pointer-events-none [mask-image:linear-gradient(to_top,black_30%,transparent)]" aria-hidden="true">
                  <Bars active={isPlaying} count={72} className="h-full w-full justify-between items-end" maxOpacity={0.8} />
                </div>
                <div className="relative px-6 py-8 md:px-14 md:py-10 max-w-3xl">
                  <button
                    type="button"
                    onClick={() => toggleAudio(it)}
                    data-testid={`reference-audio-toggle-${it.id}`}
                    aria-label={isPlaying ? "Pausieren" : "Abspielen"}
                    aria-pressed={isPlaying}
                    className="group flex items-center gap-5 md:gap-7 text-left w-full"
                  >
                    <PlayButton active={isPlaying} size="w-12 h-12 md:w-14 md:h-14" icon={18} />
                    <span className="flex-1 min-w-0">
                      <ProgressLine time={isCurrent ? time : 0} dur={isCurrent ? dur : durMap[it.id]} onSeek={seek} testId={`reference-audio-progress-${it.id}`} />
                      <span className="flex justify-between text-[11px] tracking-[0.15em] text-grey mt-1">
                        <span>{isCurrent ? fmtTime(time) : "0:00"}</span>
                        <span>{fmtTime(isCurrent ? dur : durMap[it.id])}</span>
                      </span>
                    </span>
                  </button>
                </div>
                <audio src={url(it.id)} preload="metadata" className="hidden" onLoadedMetadata={(e) => setDurMap((m) => ({ ...m, [it.id]: e.target.duration }))} aria-hidden="true" />
              </div>
            );
          })()}
        </FadeUp>
      )}

      {!featuredAudio && audios.length > 0 && (
        <FadeUp className={visuals.length > 0 ? "mt-14 md:mt-20" : ""}>
          <ul data-testid={`reference-tracklist-${category}`}>
            {audios.map((it, i) => {
              const isCurrent = currentId === it.id;
              const isPlaying = isCurrent && playing;
              return (
                <li key={it.id} className="border-t hairline last:border-b" data-testid={`reference-audio-${it.id}`}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => toggleAudio(it)}
                    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleAudio(it); } }}
                    data-testid={`reference-audio-toggle-${it.id}`}
                    aria-label={isPlaying ? "Pausieren" : "Abspielen"}
                    aria-pressed={isPlaying}
                    className="group w-full flex items-center gap-4 md:gap-8 py-5 md:py-6 cursor-pointer"
                  >
                    <span className="label-text text-gold/60 w-7 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                    <PlayButton active={isPlaying} size="w-11 h-11 md:w-12 md:h-12" icon={15} />
                    <span className="flex-1 min-w-0" aria-hidden="true">
                      {isCurrent && <ProgressLine time={time} dur={dur} onSeek={seek} testId={`reference-audio-progress-${it.id}`} />}
                    </span>
                    <Bars active={isPlaying} />
                    <span className="label-text text-grey shrink-0 w-10 text-right">{fmtTime(isCurrent && dur ? dur : durMap[it.id])}</span>
                  </div>
                  <audio src={url(it.id)} preload="metadata" className="hidden" onLoadedMetadata={(e) => setDurMap((m) => ({ ...m, [it.id]: e.target.duration }))} aria-hidden="true" />
                </li>
              );
            })}
          </ul>
        </FadeUp>
      )}

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="reference-modal"
            className="fixed inset-0 z-[300] bg-black/95 backdrop-blur-md flex items-center justify-center p-4 md:p-10"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            onClick={() => setActive(null)}
          >
            <button data-testid="reference-modal-close" className="absolute top-6 right-6 text-paper/70 hover:text-gold transition-colors duration-300 p-2 z-10" aria-label="Schließen">
              <X size={30} strokeWidth={1.5} />
            </button>
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="max-w-5xl w-full flex flex-col items-center"
            >
              {active.kind === "image" && (
                <img src={url(active.id)} alt="Referenzprojekt von DIMEDIAL" className="max-h-[80vh] w-auto max-w-full object-contain" />
              )}
              {active.kind === "video" && (
                <video controls autoPlay preload="metadata" src={url(active.id)} playsInline className="w-full max-h-[80vh] bg-ink-2" />
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default ReferenceGallery;
